// src/components/UpdateVulnerabilityModal.js
import React, { useState, useEffect } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { X, AlertTriangle, Shield } from 'lucide-react';

const UpdateVulnerabilityModal = ({ isOpen, onClose, vulnerability, onVulnerabilityUpdated }) => {
  const { getToken } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const [formData, setFormData] = useState({
    cve_id: '',
    title: '',
    severity: 'High',
    cvss_score: '',
    published_date: '',
    last_modified: '',
    affected_products: '',
    cpes: '',
    vendor: '',
    description: '',
    exploit_available: false,
    patch_available: false,
    in_the_wild: false,
    workaround: '',
    reference_urls: '',
    cwe: ''
  });

  useEffect(() => {
    if (vulnerability && isOpen) {
      setFormData({
        cve_id: vulnerability.cve_id || '',
        title: vulnerability.title || '',
        severity: vulnerability.severity || 'High',
        cvss_score: vulnerability.cvss_score || '',
        published_date: vulnerability.published_date || '',
        last_modified: vulnerability.last_modified || '',
        affected_products: Array.isArray(vulnerability.affected_products) 
          ? vulnerability.affected_products.join(', ') 
          : '',
        cpes: Array.isArray(vulnerability.cpes) 
          ? vulnerability.cpes.join(', ') 
          : '',
        vendor: vulnerability.vendor || '',
        description: vulnerability.description || '',
        exploit_available: vulnerability.exploit_available || false,
        patch_available: vulnerability.patch_available || false,
        in_the_wild: vulnerability.in_the_wild || false,
        workaround: vulnerability.workaround || '',
        reference_urls: Array.isArray(vulnerability.reference_urls) 
          ? vulnerability.reference_urls.join(', ') 
          : '',
        cwe: vulnerability.cwe || ''
      });
    }
  }, [vulnerability, isOpen]);

  if (!isOpen || !vulnerability) return null;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const token = await getToken();
      
      // Parse arrays from comma-separated strings
      const affected_products = formData.affected_products
        .split(',')
        .map(p => p.trim())
        .filter(p => p);
      
      const cpes = formData.cpes
        .split(',')
        .map(c => c.trim())
        .filter(c => c);
      
      const reference_urls = formData.reference_urls
        .split(',')
        .map(r => r.trim())
        .filter(r => r);

      const payload = {
        ...formData,
        cvss_score: formData.cvss_score ? parseFloat(formData.cvss_score) : null,
        published_date: formData.published_date || null,
        last_modified: formData.last_modified || null,
        affected_products,
        cpes,
        reference_urls,
        vendor: formData.vendor || null,
        description: formData.description || null,
        workaround: formData.workaround || null,
        cwe: formData.cwe || null
      };

      const response = await fetch(`/api/vulnerabilities/${vulnerability.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to update vulnerability');
      }

      console.log('✅ Vulnerability updated:', data);
      onVulnerabilityUpdated(data);
      onClose();
    } catch (err) {
      console.error('❌ Error updating vulnerability:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-slate-900 rounded-xl border border-slate-700/50 w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-700/50 flex items-center justify-between bg-gradient-to-r from-blue-500/10 to-purple-500/10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
              <Shield className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-100">Update CVE</h2>
              <p className="text-sm text-slate-400">Update vulnerability information</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-200 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6">
          {error && (
            <div className="mb-4 p-4 bg-red-500/10 border border-red-500/20 rounded-lg flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-red-400">Error</p>
                <p className="text-sm text-red-300 mt-1">{error}</p>
              </div>
            </div>
          )}

          <div className="space-y-4">
            {/* CVE ID */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                CVE ID <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                name="cve_id"
                value={formData.cve_id}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 focus:outline-none focus:border-blue-500/50 focus:ring-1 focus:ring-blue-500/20"
              />
            </div>

            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Title <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 focus:outline-none focus:border-blue-500/50 focus:ring-1 focus:ring-blue-500/20"
              />
            </div>

            {/* Severity and CVSS Score */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Severity <span className="text-red-400">*</span>
                </label>
                <select
                  name="severity"
                  value={formData.severity}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 focus:outline-none focus:border-blue-500/50"
                >
                  <option value="Critical">Critical</option>
                  <option value="High">High</option>
                  <option value="Medium">Medium</option>
                  <option value="Low">Low</option>
                  <option value="Informational">Informational</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  CVSS Score
                </label>
                <input
                  type="number"
                  name="cvss_score"
                  value={formData.cvss_score}
                  onChange={handleChange}
                  step="0.1"
                  min="0"
                  max="10"
                  className="w-full px-4 py-2 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 focus:outline-none focus:border-blue-500/50 focus:ring-1 focus:ring-blue-500/20"
                />
              </div>
            </div>

            {/* Dates */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Published Date
                </label>
                <input
                  type="date"
                  name="published_date"
                  value={formData.published_date}
                  onChange={handleChange}
                  className="w-full px-4 py-2 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 focus:outline-none focus:border-blue-500/50 focus:ring-1 focus:ring-blue-500/20"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Last Modified
                </label>
                <input
                  type="date"
                  name="last_modified"
                  value={formData.last_modified}
                  onChange={handleChange}
                  className="w-full px-4 py-2 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 focus:outline-none focus:border-blue-500/50 focus:ring-1 focus:ring-blue-500/20"
                />
              </div>
            </div>

            {/* Vendor */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Vendor
              </label>
              <input
                type="text"
                name="vendor"
                value={formData.vendor}
                onChange={handleChange}
                className="w-full px-4 py-2 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 focus:outline-none focus:border-blue-500/50 focus:ring-1 focus:ring-blue-500/20"
              />
            </div>

            {/* CWE */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                CWE
              </label>
              <input
                type="text"
                name="cwe"
                value={formData.cwe}
                onChange={handleChange}
                className="w-full px-4 py-2 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 focus:outline-none focus:border-blue-500/50 focus:ring-1 focus:ring-blue-500/20"
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Description
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={4}
                className="w-full px-4 py-2 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 focus:outline-none focus:border-blue-500/50 focus:ring-1 focus:ring-blue-500/20 resize-none"
              />
            </div>

            {/* Affected Products */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Affected Products (comma-separated)
              </label>
              <input
                type="text"
                name="affected_products"
                value={formData.affected_products}
                onChange={handleChange}
                className="w-full px-4 py-2 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 focus:outline-none focus:border-blue-500/50 focus:ring-1 focus:ring-blue-500/20"
              />
              <p className="text-xs text-slate-500 mt-1">Separate multiple products with commas</p>
            </div>

            {/* CPEs */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                CPEs (comma-separated)
              </label>
              <input
                type="text"
                name="cpes"
                value={formData.cpes}
                onChange={handleChange}
                placeholder="cpe:2.3:a:apache:struts:2.5.30:*:*:*:*:*:*:*, cpe:2.3:a:..."
                className="w-full px-4 py-2 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:border-blue-500/50 focus:ring-1 focus:ring-blue-500/20"
              />
              <p className="text-xs text-slate-500 mt-1">Common Platform Enumeration identifiers - separate with commas</p>
            </div>

            {/* Workaround */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Workaround / Remediation
              </label>
              <textarea
                name="workaround"
                value={formData.workaround}
                onChange={handleChange}
                rows={3}
                className="w-full px-4 py-2 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 focus:outline-none focus:border-blue-500/50 focus:ring-1 focus:ring-blue-500/20 resize-none"
              />
            </div>

            {/* Reference URLs */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Reference URLs (comma-separated)
              </label>
              <input
                type="text"
                name="reference_urls"
                value={formData.reference_urls}
                onChange={handleChange}
                className="w-full px-4 py-2 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 focus:outline-none focus:border-blue-500/50 focus:ring-1 focus:ring-blue-500/20"
              />
              <p className="text-xs text-slate-500 mt-1">Separate multiple URLs with commas</p>
            </div>

            {/* Checkboxes */}
            <div className="grid grid-cols-3 gap-4">
              <div className="flex items-center gap-3 p-4 bg-slate-800/30 rounded-lg border border-slate-700/50">
                <input
                  type="checkbox"
                  id="exploit_available"
                  name="exploit_available"
                  checked={formData.exploit_available}
                  onChange={handleChange}
                  className="w-4 h-4 text-blue-500 bg-slate-800 border-slate-600 rounded focus:ring-blue-500 focus:ring-offset-slate-900"
                />
                <label htmlFor="exploit_available" className="text-sm text-slate-300 cursor-pointer">
                  Exploit Available
                </label>
              </div>

              <div className="flex items-center gap-3 p-4 bg-slate-800/30 rounded-lg border border-slate-700/50">
                <input
                  type="checkbox"
                  id="patch_available"
                  name="patch_available"
                  checked={formData.patch_available}
                  onChange={handleChange}
                  className="w-4 h-4 text-blue-500 bg-slate-800 border-slate-600 rounded focus:ring-blue-500 focus:ring-offset-slate-900"
                />
                <label htmlFor="patch_available" className="text-sm text-slate-300 cursor-pointer">
                  Patch Available
                </label>
              </div>

              <div className="flex items-center gap-3 p-4 bg-slate-800/30 rounded-lg border border-slate-700/50">
                <input
                  type="checkbox"
                  id="in_the_wild"
                  name="in_the_wild"
                  checked={formData.in_the_wild}
                  onChange={handleChange}
                  className="w-4 h-4 text-orange-500 bg-slate-800 border-slate-600 rounded focus:ring-orange-500 focus:ring-offset-slate-900"
                />
                <label htmlFor="in_the_wild" className="text-sm text-slate-300 cursor-pointer">
                  In The Wild
                </label>
              </div>
            </div>
          </div>
        </form>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-700/50 bg-slate-900/50 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-slate-700/50 hover:bg-slate-700 rounded-lg text-sm text-slate-300 transition-colors"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="px-4 py-2 bg-blue-500 hover:bg-blue-600 rounded-lg text-sm text-white font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Updating...' : 'Update CVE'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UpdateVulnerabilityModal;